import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AddressLabelsButton } from "@/components/AddressLabelsButton";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Plus, UserPlus, Phone, FileUp } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

export default function Customers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCompany = user?.role === "owner";
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", tags: "" });
  const [saving, setSaving] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const bulkAdd = async () => {
    setBulkBusy(true);
    try {
      const contacts = bulkText.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
        const parts = l.split(",").map((x) => x.trim());
        return { name: parts[0] || "Pilgrim", phone: parts[1] || "", email: parts[2] || "", tags: parts[3] ? [parts[3]] : [] };
      }).filter((c) => c.phone);
      if (!contacts.length) { toast.error("Add lines like: Name, +91XXXXXXXXXX"); setBulkBusy(false); return; }
      const { data } = await api.post("/contacts/bulk", { workspace_id: localStorage.getItem("workspace_id"), contacts });
      toast.success(`Added ${data.created} pilgrim(s)${data.skipped ? `, ${data.skipped} skipped (already exist)` : ""}`);
      setBulkOpen(false); setBulkText(""); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };

  const load = async (q = "") => {
    const { data } = await api.get(`/contacts?search=${encodeURIComponent(q)}`);
    setContacts(data);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/contacts", {
        workspace_id: localStorage.getItem("workspace_id"),
        name: form.name,
        phone: form.phone,
        email: form.email,
        tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
      });
      toast.success("Customer added");
      setOpen(false);
      setForm({ name: "", phone: "", email: "", tags: "" });
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Customer Directory" title="Customers"
        subtitle="Add a pilgrim's phone here so they can log into the Pilgrim Portal (/portal)"
        image="https://images.unsplash.com/photo-1581553673739-c4906b5d0de8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHx0cmF2ZWwlMjBkb2N1bWVudHMlMjBwYXNzcG9ydHxlbnwwfHx8fDE3OTAzNDgwMjN8MA&ixlib=rb-4.1.0&q=85"
        action={
          <div className="flex gap-2 flex-wrap">
            {isCompany && <AddressLabelsButton />}
            <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
              <DialogTrigger asChild>
                <Button data-testid="bulk-add-btn" variant="outline" className="border-gold/40 text-gold hover:bg-gold/15 font-semibold gap-2">
                  <FileUp className="w-4 h-4" /> Bulk Add Pilgrims
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#05261D] border-gold/20">
                <DialogHeader><DialogTitle className="font-serif text-gold flex items-center gap-2"><FileUp className="w-5 h-5" /> Bulk Add Pilgrims</DialogTitle></DialogHeader>
                <p className="text-xs text-muted-foreground">One pilgrim per line: <span className="font-mono text-gold/80">Name, Phone, Email(optional), Tag(optional)</span></p>
                <Textarea data-testid="bulk-textarea" rows={7} value={bulkText} onChange={(e) => setBulkText(e.target.value)}
                  placeholder={"Abdul Rahman, +919812345678\nFatima Khatun, +919845678901, fatima@example.com, VIP"} className="bg-card/60 font-mono text-xs" />
                <DialogFooter>
                  <Button data-testid="bulk-save-btn" onClick={bulkAdd} disabled={bulkBusy} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold">{bulkBusy ? "Importing…" : "Import All"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-customer-btn" className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-2">
                <Plus className="w-4 h-4" /> Add Customer
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#05261D] border-gold/20">
              <DialogHeader><DialogTitle className="font-serif text-gold flex items-center gap-2"><UserPlus className="w-5 h-5" /> New Customer</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs text-muted-foreground">Full name</Label><Input data-testid="customer-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-card/60" /></div>
                <div><Label className="text-xs text-muted-foreground">Phone</Label><Input data-testid="customer-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 bg-card/60" placeholder="+9198…" /></div>
                <div><Label className="text-xs text-muted-foreground">Email</Label><Input data-testid="customer-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-card/60" /></div>
                <div><Label className="text-xs text-muted-foreground">Tags (comma separated)</Label><Input data-testid="customer-tags-input" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="mt-1 bg-card/60" placeholder="VIP, Deluxe" /></div>
              </div>
              <DialogFooter>
                <Button data-testid="save-customer-btn" onClick={create} disabled={saving || !form.name || !form.phone} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold">{saving ? "Saving…" : "Create"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      <div className="p-4 sm:p-8">
        <div className="relative mb-5 max-w-md">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
          <Input data-testid="customers-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or phone…" className="pl-9 bg-card/50" />
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {contacts.map((c) => (
            <Card key={c.id} data-testid={`customer-card-${c.id}`} onClick={() => navigate(`/customers/${c.id}`)}
              className="p-4 bg-card/40 border-border/60 card-hover cursor-pointer">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-primary/50 border border-gold/30 flex items-center justify-center text-lg font-serif text-gold">{c.name[0]}</div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{c.customer_no && <span className="text-gold font-mono text-xs mr-1.5" data-testid={`customer-no-${c.id}`}>#{c.customer_no}</span>}{c.name}</p>
                  <p className="text-xs text-muted-foreground font-mono flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1 mt-3">
                {(c.tags || []).map((t) => <Badge key={t} variant="outline" className="text-[10px] border-gold/40 text-gold">{t}</Badge>)}
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50 text-[11px] text-muted-foreground">
                <span>{c.agent_name ? `Partner: ${c.agent_name}` : "Unassigned"}</span>
                <span>{timeAgo(c.last_contact_at)}</span>
              </div>
            </Card>
          ))}
        </div>
        {contacts.length === 0 && <p className="text-sm text-muted-foreground text-center py-12">No customers yet.</p>}
      </div>
    </div>
  );
}
